'use client';

import Image from 'next/image';
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from 'motion/react';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
} from 'react';
import {
  HERO_BOX_IMAGES,
  HERO_CARD_SPRING_SPREAD,
  HERO_CARD_SPRING_STACK,
  HERO_CARD_STAGGER_SPREAD_SEC,
  HERO_CARD_STAGGER_STACK_SEC,
  HERO_HUMAN_FADE_IN,
  HERO_STAGE_TIMING,
  getHeroMarqueeWheelGeometry,
  HERO_MOBILE_ARC_CURVE_MULT,
  heroMarqueeArcFraction,
  sampleHeroMarqueeAlongArc,
  type HeroMarqueeBoxDim,
} from './hero-animated-stage';
import { gsap, ScrollTrigger } from '../lib/gsap';
import {
  MOBILE_REF_WIDTH_PX,
  mobileRefScale,
} from '../lib/mobile-layout';
import { getViewportHeight } from '../lib/viewport-height';
import { HeroTokenCardFace } from './hero-token-card';

/** 20% smaller than previous 280px cap (280 × 0.8). */
const CARD_PX_REF = Math.round(280 * 0.8);

/**
 * Round computed geometry before it goes into a style string. The server and the browser can differ
 * in the last few float digits of trigonometric results (~1e-13), which React reports as a hydration
 * mismatch; 2 decimals is far below a visible pixel and makes both sides serialize identically.
 */
const round2 = (n: number): number => Math.round(n * 100) / 100;
/** Reference viewport width — layout is authored at this width then scaled to fit. */
const MOBILE_HERO_DESIGN_WIDTH = MOBILE_REF_WIDTH_PX;
const HUMAN_W_REF = 440;
const HUMAN_H_REF = 380;

function useViewportWidth(): number {
  return useSyncExternalStore(
    (onStoreChange) => {
      const handler = () => onStoreChange();
      window.addEventListener('resize', handler);
      return () => window.removeEventListener('resize', handler);
    },
    () => window.innerWidth,
    () => MOBILE_HERO_DESIGN_WIDTH
  );
}

/** Extra px below the hero bottom so stack-rise starts off-screen (viewport bottom). */
function useEnterBelowViewportPx(
  rootRef: RefObject<HTMLDivElement | null>,
  cardH: number
): number {
  const [offset, setOffset] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      const el = rootRef.current;
      if (!el) return;
      const bottom = el.getBoundingClientRect().bottom;
      const belowViewport = getViewportHeight() - bottom;
      setOffset(Math.max(0, belowViewport) + cardH);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [rootRef, cardH]);

  return offset;
}

/** Uniform scale so every phone fills the viewport from the 430px reference. */
function mobileHeroViewportScale(vw: number): number {
  return mobileRefScale(vw);
}

type IntroStage = 0 | 1 | 2 | 3;

function frac01(x: number): number {
  return ((x % 1) + 1) % 1;
}

type HeroMobileTileProps = {
  index: number;
  src: string;
  stage: IntroStage;
  phase: ReturnType<typeof useMotionValue<number>>;
  geo: ReturnType<typeof getHeroMarqueeWheelGeometry>;
  box: HeroMarqueeBoxDim;
  cardPx: number;
  enterBelowViewportPx: number;
};

function HeroMobileTile({
  index,
  src,
  stage,
  phase,
  geo,
  box,
  cardPx,
  enterBelowViewportPx,
}: HeroMobileTileProps) {
  const start = useMemo(
    () => sampleHeroMarqueeAlongArc(geo, box, heroMarqueeArcFraction(index)),
    [geo, box, index]
  );
  const centerStart = useMemo(
    () => sampleHeroMarqueeAlongArc(geo, box, heroMarqueeArcFraction(3)),
    [geo, box]
  );

  const stackLeft = (geo.spanX - box.w) / 2;
  const stackTop = centerStart.top + box.h * 0.12;
  const stackX = stackLeft - start.left;
  const stackY = stackTop - start.top;
  const enterTop = geo.stripH + box.h * 0.8 - geo.boxLiftY;
  const enterY = enterTop - start.top + enterBelowViewportPx;

  const offsetX = useMotionValue(stackX);
  const offsetY = useMotionValue(enterY);
  const rotOffset = useMotionValue(-start.tangentDeg);
  const opacity = useMotionValue(0);

  useEffect(() => {
    if (stage === 0) {
      offsetX.set(stackX);
      offsetY.set(enterY);
      rotOffset.set(-start.tangentDeg);
      opacity.set(0);
      return;
    }

    if (stage === 1) {
      const delay = index * HERO_CARD_STAGGER_STACK_SEC;
      void animate(offsetX, stackX, { ...HERO_CARD_SPRING_STACK, delay });
      void animate(offsetY, stackY, { ...HERO_CARD_SPRING_STACK, delay });
      void animate(rotOffset, -start.tangentDeg, {
        ...HERO_CARD_SPRING_STACK,
        delay,
      });
      void animate(opacity, 1, { duration: 0, delay });
      return;
    }

    if (stage === 2) {
      const delay = index * HERO_CARD_STAGGER_SPREAD_SEC;
      void animate(offsetX, 0, { ...HERO_CARD_SPRING_SPREAD, delay });
      void animate(offsetY, 0, { ...HERO_CARD_SPRING_SPREAD, delay });
      void animate(rotOffset, 0, { ...HERO_CARD_SPRING_SPREAD, delay });
    }
  }, [
    stage,
    index,
    stackX,
    stackY,
    enterY,
    enterBelowViewportPx,
    start.tangentDeg,
    offsetX,
    offsetY,
    rotOffset,
    opacity,
  ]);

  const transform = useTransform(
    [phase, offsetX, offsetY, rotOffset],
    ([p, ox, oy, ro]) => {
      const s = frac01(heroMarqueeArcFraction(index) - (p as number));
      const { left, top, tangentDeg } = sampleHeroMarqueeAlongArc(geo, box, s);
      return `translate3d(${round2(left + (ox as number))}px, ${round2(top + (oy as number))}px, 0) rotate(${round2(tangentDeg + (ro as number))}deg)`;
    }
  );

  return (
    <motion.div
      className="absolute left-0 top-0 origin-center overflow-hidden rounded-10 shadow-sm transform-gpu [backface-visibility:hidden]"
      style={{
        width: box.w,
        height: box.h,
        zIndex: 5 + index,
        transform,
        opacity,
      }}
    >
      <HeroTokenCardFace src={src} size={cardPx} />
    </motion.div>
  );
}

type HeroMobileMarqueeProps = {
  onHumansReveal?: () => void;
};

export function HeroMobileMarquee({ onHumansReveal }: HeroMobileMarqueeProps) {
  const reduceMotion = useReducedMotion();
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportWidth = useViewportWidth();
  const viewportScale = mobileHeroViewportScale(viewportWidth);
  const cardPx = CARD_PX_REF;
  const humanWidth = Math.min(HUMAN_W_REF, MOBILE_HERO_DESIGN_WIDTH - 40);
  const humanHeight = Math.round(humanWidth * (HUMAN_H_REF / HUMAN_W_REF));
  const [stage, setStage] = useState<IntroStage>(0);
  const [marqueeActive, setMarqueeActive] = useState(Boolean(reduceMotion));
  const [spreadStageEntered, setSpreadStageEntered] = useState(false);
  const hasNotifiedRef = useRef(false);

  const box: HeroMarqueeBoxDim = useMemo(
    () => ({ w: cardPx, h: cardPx, gap: 8 }),
    [cardPx]
  );
  const enterBelowViewportPx = useEnterBelowViewportPx(rootRef, box.h);

  const geo = useMemo(
    () =>
      getHeroMarqueeWheelGeometry(box, 1, {
        bCurveMult: HERO_MOBILE_ARC_CURVE_MULT,
      }),
    [box]
  );
  const phase = useMotionValue(0);

  const humansVisible = Boolean(reduceMotion) || stage >= 3;

  useEffect(() => {
    if (reduceMotion) {
      setStage(3);
      setMarqueeActive(false);
      return;
    }
    const t = setTimeout(() => setStage(1), HERO_STAGE_TIMING.beforeStack);
    return () => clearTimeout(t);
  }, [reduceMotion]);

  useEffect(() => {
    if (reduceMotion || stage !== 1) return;
    const t = setTimeout(() => setStage(2), HERO_STAGE_TIMING.stack);
    return () => clearTimeout(t);
  }, [stage, reduceMotion]);

  // Start the endless drift on a timer once the spread has (nearly) settled. It used to wait for all
  // seven spring promises to resolve, but a promise that is superseded (e.g. by a viewport-height
  // change during the spread) never resolves, so on some phones the loop never started.
  //
  // The timer is keyed on `spreadStageEntered` (a one-way latch), not on `stage` directly: `stage`
  // advances 2 -> 3 (humans fade in) only `humansAfterSpreadStart` after entering 2, which is SHORTER
  // than `marqueeAfterSpread` — so an effect gated on `stage === 2` gets torn down (and its pending
  // setTimeout cancelled as a cleanup) by that 2 -> 3 transition before the timer ever fires, and the
  // loop silently never starts. Once latched true this never reverts, so the effect below is never
  // cancelled by a later stage change.
  useEffect(() => {
    if (stage >= 2) setSpreadStageEntered(true);
  }, [stage]);

  useEffect(() => {
    if (reduceMotion || !spreadStageEntered) return;
    const t = setTimeout(() => setMarqueeActive(true), HERO_STAGE_TIMING.marqueeAfterSpread);
    return () => clearTimeout(t);
  }, [reduceMotion, spreadStageEntered]);

  useEffect(() => {
    if (reduceMotion || stage !== 2) return;
    const t = setTimeout(
      () => setStage(3),
      HERO_STAGE_TIMING.humansAfterSpreadStart
    );
    return () => clearTimeout(t);
  }, [stage, reduceMotion]);

  useEffect(() => {
    if (stage !== 3) return;
    ScrollTrigger.refresh();
  }, [stage]);

  useEffect(() => {
    if (hasNotifiedRef.current) return;
    if (reduceMotion || humansVisible) {
      hasNotifiedRef.current = true;
      onHumansReveal?.();
    }
  }, [onHumansReveal, reduceMotion, humansVisible]);

  useEffect(() => {
    if (reduceMotion || !marqueeActive) return;
    const state = { p: phase.get() };
    const tween = gsap.to(state, {
      p: 1,
      duration: 96,
      repeat: -1,
      ease: 'none',
      onUpdate: () => phase.set(state.p),
    });
    return () => {
      tween.kill();
    };
  }, [phase, reduceMotion, marqueeActive]);

  /** Nudge card strip down so rotated tops are not clipped on narrow viewports. */
  const boxDropY = Math.round(geo.stripH * 0.1);
  const heroMinHeight = Math.max(
    Math.round(geo.stripH) + 16 + boxDropY,
    Math.round(humanHeight * 0.72)
  );

  return (
    <div
      className="mx-auto overflow-hidden"
      style={{
        width: Math.round(MOBILE_HERO_DESIGN_WIDTH * viewportScale),
        height: Math.round(heroMinHeight * viewportScale),
      }}
    >
      <div
        ref={rootRef}
        className="relative isolate overflow-hidden"
        style={{
          width: MOBILE_HERO_DESIGN_WIDTH,
          minHeight: heroMinHeight,
          transform: `scale(${viewportScale})`,
          transformOrigin: 'top left',
        }}
        aria-label="Proply hero illustration"
      >
      <div
        className="absolute inset-x-0 bottom-0 top-0 z-10 overflow-hidden"
      >
        <div
          className="absolute bottom-0 left-1/2 transform-gpu [backface-visibility:hidden]"
          style={{
            width: geo.spanX,
            height: geo.stripH,
            transform: `translateX(-50%) translateY(${boxDropY}px)`,
            transformOrigin: 'center bottom',
          }}
        >
          {reduceMotion
            ? HERO_BOX_IMAGES.map((src, i) => {
                const { left, top, tangentDeg } = sampleHeroMarqueeAlongArc(
                  geo,
                  box,
                  heroMarqueeArcFraction(i)
                );
                return (
                  <div
                    key={src}
                    className="absolute left-0 top-0 origin-center overflow-hidden rounded-10 shadow-sm"
                    style={{
                      width: box.w,
                      height: box.h,
                      zIndex: 5 + i,
                      transform: `translate3d(${round2(left)}px, ${round2(top)}px, 0) rotate(${round2(tangentDeg)}deg)`,
                    }}
                  >
                    <HeroTokenCardFace src={src} size={cardPx} />
                  </div>
                );
              })
            : HERO_BOX_IMAGES.map((src, i) => (
                <HeroMobileTile
                  key={src}
                  index={i}
                  src={src}
                  stage={stage}
                  phase={phase}
                  geo={geo}
                  box={box}
                  cardPx={cardPx}
                  enterBelowViewportPx={enterBelowViewportPx}
                />
              ))}
        </div>
      </div>

      <motion.div
        className="pointer-events-none absolute inset-0 z-20 flex items-end justify-center transform-gpu [backface-visibility:hidden]"
        initial={false}
        animate={{ opacity: humansVisible ? 1 : 0 }}
        transition={HERO_HUMAN_FADE_IN}
        aria-hidden={!humansVisible}
      >
        {/* translate-y-[27px] (reference frame): the SVG bakes in its own fade-to-transparent
            gradient (a `mask` linear-gradient from y=0.88 to y=0.92 of the 548px viewBox — the same
            soft edge desktop shows via HeroAnimatedStage's uncropped, uncentred copy of this asset),
            then is FULLY transparent from y=0.92 to the bottom (the last 8% of the viewBox, ~27px of
            this box's 337px rendered height). That fully-blank tail — not the fade itself — is what
            reserved dead space below the visible artwork and read as an oversized gap to the
            headline. 27px nudges exactly that blank tail out past the marquee's `overflow-hidden`
            bounds; going further eats into the 0.88–0.92 band and crops the fade itself away,
            leaving a hard edge instead (verify with a per-row alpha scan of the raw SVG, not just a
            single "last visible row" threshold, if this ever needs re-tuning). */}
        <Image
          src="/images/img_container.svg"
          alt="People exploring property listings on their phones"
          width={HUMAN_W_REF}
          height={HUMAN_H_REF}
          className="translate-y-[27px] object-contain"
          style={{ width: humanWidth, height: humanHeight }}
          priority
        />
      </motion.div>
      </div>
    </div>
  );
}
